import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModal } from '../../actions';
import {
  setUserSetting,
  updateUserSettings,
} from '../../actions/Auth';
import {
  clearCollectionsList,
  clearSelectedCollections,
  fetchCollections,
  fetchFeaturedCollections,
  setCollectionsParams,
  setSidebarCollectionOpen,
  subscribeCollection,
  unsubscribeCollection,
  updateCollection,
  updateCurrentCollection,
  updateSidebarCollection,
} from '../../actions/Collections';
import {
  clearFiles,
  clearFilesList,
  fetchFiles,
  setFilesLoading,
  setFilesParams,
} from '../../actions/Files';

import Sort from '../Filters/Sort';
import ViewMode from '../Filters/ViewMode';
import HeaderSelectAll from './HeaderSelectAll';

import {
  isArchive,
  isTrash,
} from '../../helpers/App';
import {
  getCurrentUser,
  userCan,
  userHasCap,
} from '../../helpers/Auth';
import {
  getParentCollection,
  isCollectionPage,
} from '../../helpers/Collections';
import { getFilesView } from '../../helpers/Files';
import { addFlashMessage } from '../../helpers/FlashMessages';

const FilesHeaderActions = ({
  intl,
  profile,
  collection,
  handleDownloadRequest,
  handleRequestDownloadCollection,
  handleDownloadCollection,
  handleChangeAccess,
  handleShareCollection,
  handleShareRequest,
  handleChangeOwner,
  handleFeatureCollection,
  handleOpenSidebarCollection,
  handleUnsubscribe,
  handleSubscribe,
  handleArchive,
  openCollectionEditModal,
  openCollectionDuplicateModal,
  openEmptyTrashModal,
  openExportModal,
  updateCurrentCollectionAction,
  filesGroup,
  filesParams,
  afterCreateCollection,
  setOrder,
  setFilesView,
  search,
}) => {
  const filesView = getFilesView();

  const {
    order,
    orderby,
  } = filesParams;

  let firstParent = null;
  if (collection) {
    firstParent = collection.ancestors ? collection.ancestors[0] : null;
  }

  return (
    <div className="header-actions files-actions">
      {!isCollectionPage() && (
        <div className="header-actions-buttons">
          <Sort
            orderby={orderby}
            order={order}
            search={search}
            setOrder={setOrder}
            collection={collection}
            size="sm"
          />
          <ViewMode view={filesView} setView={setFilesView} size="sm" />
        </div>
      )}
      {(collection && !collection.deleted_at) && (
        <UncontrolledDropdown>
          <DropdownToggle className="collection-actions" size="sm" aria-label={intl.formatMessage({ defaultMessage: 'Collection actions' })}>
            <i className="far fa-ellipsis-h" />
          </DropdownToggle>
          <DropdownMenu>
            {((userCan('collection.download', collection) || userCan('collection.request_download', collection)) && collection.request) && (
              <DropdownItem
                onClick={() => {
                  handleDownloadRequest(collection);
                }}
              >
                <i className="fas fa-download" />
                {intl.formatMessage({ defaultMessage: 'Download Collection' })}
              </DropdownItem>
            )}
            {userCan('collection.request_download', collection) && (
              <DropdownItem
                onClick={() => {
                  handleRequestDownloadCollection(collection);
                }}
              >
                <i className="fas fa-download" />
                {intl.formatMessage({ defaultMessage: 'Request Download Collection' })}
              </DropdownItem>
            )}
            {(userCan('collection.download', collection) && !collection.request) && (
              <DropdownItem
                onClick={() => {
                  handleDownloadCollection(collection);
                }}
              >
                <i className="fas fa-download" />
                {intl.formatMessage({ defaultMessage: 'Download Collection' })}
              </DropdownItem>
            )}
            {(userCan('collection.share', collection) && !collection.archived) && (
              <DropdownItem
                onClick={() => {
                  if (userCan('collection.change_access', collection)) {
                    handleChangeAccess(collection.uuid);
                  } else {
                    handleShareCollection(collection);
                  }
                }}
              >
                <i className="fas fa-share-alt" />
                {userCan('collection.change_access', collection) ? (
                  intl.formatMessage({ defaultMessage: 'Access & Share' })
                ) : (
                  intl.formatMessage({ defaultMessage: 'Share a link' })
                )}
              </DropdownItem>
            )}
            {(!userCan('collection.share', collection) && userCan('share_download_requests') && !collection.archived && collection.request) && (
              <DropdownItem
                onClick={() => {
                  handleShareRequest({
                    ...collection.request,
                    user: profile,
                    collections: [collection],
                  });
                }}
              >
                <i className="fas fa-share-alt" />
                {intl.formatMessage({ defaultMessage: 'Share a link' })}
              </DropdownItem>
            )}
            {userCan('collection.edit', collection) && (
              <DropdownItem
                onClick={() => {
                  openCollectionEditModal({
                    className: 'modal-edit-collection',
                    collection,
                    parentCollection: getParentCollection(collection),
                    afterCreateCollection: updateCurrentCollectionAction,
                  });
                }}
              >
                <i className="fas fa-pencil" />
                {intl.formatMessage({ defaultMessage: 'Edit Collection' })}
              </DropdownItem>
            )}
            {userCan('collection.edit', collection) && (
              <DropdownItem
                onClick={() => {
                  openCollectionDuplicateModal({
                    className: 'modal-duplicate-collection',
                    collection,
                    parentCollection: getParentCollection(collection),
                  });
                }}
              >
                <i className="fas fa-clone" />
                {intl.formatMessage({ defaultMessage: 'Duplicate Collection' })}
              </DropdownItem>
            )}
            {userCan('collection.change_owner', collection) && (
              <DropdownItem
                onClick={() => {
                  handleChangeOwner(collection);
                }}
              >
                <i className="fas fa-user-circle" />
                {intl.formatMessage({ defaultMessage: 'Change Owner' })}
              </DropdownItem>
            )}
            <CopyToClipboard
              text={collection.uuid}
              options={{
                format: 'text/plain',
              }}
            >
              <DropdownItem
                onClick={() => {
                  addFlashMessage({
                    type: 'success',
                    text: intl.formatMessage({ defaultMessage: 'Collection ID has been copied' }),
                  });
                }}
              >
                <i className="fas fa-copy" />
                {intl.formatMessage({ defaultMessage: 'Copy ID' })}
              </DropdownItem>
            </CopyToClipboard>
            {userCan('collection.feature', collection) && (
              <DropdownItem
                onClick={() => {
                  handleFeatureCollection(collection);
                }}
              >
                <i className="fas fa-star" />
                {collection.featured ? intl.formatMessage({ defaultMessage: 'Un-feature' }) :
                  intl.formatMessage({ defaultMessage: 'Feature' })}
              </DropdownItem>
            )}
            {(collection.open && !collection.archived && !collection.smart) && (
              <DropdownItem
                onClick={() => {
                  handleOpenSidebarCollection(collection);
                }}
              >
                <i className="fas fa-folder" />
                {intl.formatMessage({ defaultMessage: 'Open in Collection Builder' })}
              </DropdownItem>
            )}
            <DropdownItem
              onClick={() => {
                if (collection.subscribed) {
                  handleUnsubscribe(collection);
                } else {
                  handleSubscribe(collection);
                }
              }}
            >
              {collection.subscribed ? (
                <>
                  <i className="fas fa-bell-slash" />
                  {intl.formatMessage({ defaultMessage: 'Unsubscribe' })}
                </>
              ) : (
                <>
                  <i className="fas fa-bell" />
                  {intl.formatMessage({ defaultMessage: 'Subscribe' })}
                </>
              )}
            </DropdownItem>
            <DropdownItem
              onClick={() => { openExportModal(); }}
            >
              <i className="fas fa-file-export" />
              {intl.formatMessage({ defaultMessage: 'Export' })}
            </DropdownItem>
            {userCan('collection.archive', collection) && (
              <>
                <DropdownItem divider />
                <DropdownItem
                  onClick={() => {
                    handleArchive(collection);
                  }}
                >
                  <i className={`fas fa-${collection.archived ? 'box-open' : 'archive'}`} />
                  {collection.archived ? intl.formatMessage({ defaultMessage: 'Unarchive Collection' }) :
                    intl.formatMessage({ defaultMessage: 'Archive Collection' })}
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      )}
      {filesGroup && (
        <UncontrolledDropdown>
          <DropdownToggle className="collection-actions">
            <i className="far fa-ellipsis-h" />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              onClick={() => {
                openCollectionEditModal({
                  className: 'modal-edit-collection',
                  filesGroup,
                  archived: isArchive(),
                  notificationCallback: (c) => {
                    addFlashMessage({
                      type: 'success',
                      text: intl.formatMessage({ defaultMessage: 'A new Collection has been created' }),
                      button: {
                        url: `/collections/${c.uuid}`,
                        text: intl.formatMessage({ defaultMessage: 'Open Collection' }),
                      },
                    });
                  },
                });
              }}
            >
              <i className="fas fa-clone" />
              <span>
                {intl.formatMessage({ defaultMessage: 'Create Collection' })}
              </span>
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      )}
      {!isCollectionPage() && (
        <HeaderSelectAll />
      )}
      {(
        collection &&
        collection.open &&
        !collection.archived &&
        !collection.deleted_at &&
      (
        userHasCap('create_collection_all') ||
        (
          userHasCap('my_collections') &&
          firstParent &&
          firstParent.uuid === '-1'
        )
      )) && (
        <div className="button-wrapper">
          <Button
            color="primary"
            onClick={() => {
              openCollectionEditModal({
                className: 'modal-edit-collection',
                parentCollection: collection,
                archived: isArchive(),
                afterCreateCollection,
              });
            }}
          >
            <i className="fas fa-plus" />
            <span>
              {intl.formatMessage({ defaultMessage: 'New Collection' })}
            </span>
          </Button>
        </div>
      )}
      {(isTrash() && userHasCap('delete_files_collections_permanent')) && (
        <div className="button-wrapper">
          <Button
            color="primary"
            onClick={openEmptyTrashModal}
          >
            <i className="fas fa-trash" />
            {intl.formatMessage({ defaultMessage: 'Empty trash' })}
          </Button>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  collection: state.Collections.collection,
  filesGroup: state.Files.filesGroup,
  filesParams: state.Files.params,
  search: state.Search.search,
});

const mapDispatchToProps = (dispatch, props) => ({
  setOrder: (orderby, order) => {
    dispatch(clearFiles());
    dispatch(setFilesParams({
      orderby,
      order,
      page: 0,
    }));
    dispatch(fetchFiles());
    dispatch(setCollectionsParams({ page: 0 }));
    dispatch(clearCollectionsList());
    dispatch(clearSelectedCollections());
    dispatch(fetchCollections());
  },
  setFilesView: (view) => {
    const profile = getCurrentUser();
    if (profile) {
      dispatch(clearFilesList());
      dispatch(setFilesLoading(true));
      dispatch(setUserSetting({ files_view: view }));
      dispatch(updateUserSettings(profile.uuid, { files_view: view })).then(() => {
        dispatch(setFilesParams({ page: 0 }));
        dispatch(fetchFiles());
      });
    }
  },
  openCollectionEditModal: (params) => {
    dispatch(showModal('CollectionEditModal', params));
  },
  openCollectionDuplicateModal: (params) => {
    dispatch(showModal('CollectionDuplicateModal', params));
  },
  openEmptyTrashModal: () => {
    dispatch(showModal('EmptyTrashModal', {}));
  },
  updateCurrentCollectionAction: collection => (
    dispatch(updateCurrentCollection(collection))
  ),
  handleDownloadCollection: (collection) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      collections: [collection],
      backdrop: true,
    }));
  },
  handleDownloadRequest: (collection) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      files: collection.request.files,
      collections: [collection],
      requestUuid: collection.request.uuid,
      availableFileSizes: collection.request.file_sizes.map(fs => (`${fs.id}`)),
      backdrop: true,
    }));
  },
  handleRequestDownloadCollection: (collection) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      collections: [collection],
      backdrop: true,
      request: true,
    }));
  },
  handleShareCollection: (collection) => {
    dispatch(showModal('ShareModal', {
      className: 'modal-share modal-share-file',
      collections: [collection],
    }));
  },
  handleShareRequest: (request) => {
    dispatch(showModal('ShareModal', {
      className: 'modal-share modal-share-file',
      request,
    }));
  },
  handleChangeOwner: (collection) => {
    dispatch(showModal('ChangeOwnerModal', {
      className: 'modal-change-owner',
      collections: [collection],
    }));
  },
  handleChangeAccess: (collection) => {
    dispatch(showModal('AccessModal', {
      className: 'modal-access modal-file-access',
      collections: [collection],
    }));
  },
  handleArchive: (collection) => {
    dispatch(showModal('ArchiveModal', {
      className: 'modal-archive modal-file-archive',
      collections: [collection],
      action: collection.archived ? 'unarchive' : 'archive',
    }));
  },
  handleFeatureCollection: (collection) => {
    const parentCollection = getParentCollection(collection);
    dispatch(updateCollection({
      collection: {
        ...collection,
        parent_uuid: parentCollection?.uuid,
        featured: !collection.featured,
      },
    }))
      .then(() => {
        dispatch(fetchFeaturedCollections({ archived: isArchive() || undefined, silent: true }));
      });
    dispatch(updateCurrentCollection({ ...collection, featured: !collection.featured }));
    const { intl } = props;
    addFlashMessage({
      type: 'success',
      text: collection.featured ? intl.formatMessage({ defaultMessage: 'Collection has been un-featured' }) :
        intl.formatMessage({ defaultMessage: 'Collection has been featured' }),
    });
  },
  handleOpenSidebarCollection: (collection) => {
    dispatch(updateSidebarCollection(collection));
    dispatch(setSidebarCollectionOpen(true));
  },
  handleSubscribe: (collection) => {
    dispatch(subscribeCollection(collection.uuid)).then((response) => {
      dispatch(updateCurrentCollection(response));
    });
  },
  handleUnsubscribe: (collection) => {
    dispatch(unsubscribeCollection(collection.uuid)).then((response) => {
      dispatch(updateCurrentCollection(response));
    });
  },
  openExportModal: () => {
    dispatch(showModal('ExportModal', {
      className: 'modal-export',
    }));
  },
});

FilesHeaderActions.propTypes = {
  intl: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  collection: PropTypes.object,
  handleDownloadRequest: PropTypes.func.isRequired,
  handleRequestDownloadCollection: PropTypes.func.isRequired,
  handleDownloadCollection: PropTypes.func.isRequired,
  handleChangeAccess: PropTypes.func.isRequired,
  handleShareCollection: PropTypes.func.isRequired,
  handleShareRequest: PropTypes.func.isRequired,
  handleChangeOwner: PropTypes.func.isRequired,
  handleFeatureCollection: PropTypes.func.isRequired,
  handleOpenSidebarCollection: PropTypes.func.isRequired,
  handleUnsubscribe: PropTypes.func.isRequired,
  handleSubscribe: PropTypes.func.isRequired,
  handleArchive: PropTypes.func.isRequired,
  openCollectionEditModal: PropTypes.func.isRequired,
  openCollectionDuplicateModal: PropTypes.func.isRequired,
  openEmptyTrashModal: PropTypes.func.isRequired,
  openExportModal: PropTypes.func.isRequired,
  updateCurrentCollectionAction: PropTypes.func.isRequired,
  filesGroup: PropTypes.object,
  filesParams: PropTypes.object.isRequired,
  afterCreateCollection: PropTypes.func.isRequired,
  setOrder: PropTypes.func.isRequired,
  setFilesView: PropTypes.func.isRequired,
  search: PropTypes.string,
};

FilesHeaderActions.defaultProps = {
  collection: null,
  filesGroup: null,
  search: null,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(FilesHeaderActions));
